import { app } from "@/main.js"
import jwt_decode from 'jwt-decode';

const defaultState = () => {
	return {
		accessToken: null,
		isAuthenticated: false,
		tokenExpiry: null,
		jwtPayload: null,
		isSuperAdmin: false,
		isConsultant: false,
		isClient: false,
		isAdmin: false,
		isGeneralUser: false,
		isOrgAdmin: false,
		hasOrgSettingsAccess: false,
		token: null,
		loginLoop: 0,
		isTokenExpired: false,
		currentUser: null,
		organisationsList: null,
		currentOrganisation: {},
		tiny_mce_api_key: "",
		usersWithVertexRoles: [],
		lastActive: null,
		isRefreshing: false,
	}
}

const state = () => ({
	accessToken: null,
	isAuthenticated: false,
	tokenExpiry: null,
	jwtPayload: null,
	isSuperAdmin: false,
	isConsultant: false,
	isClient: false,
	isAdmin: false,
	isGeneralUser: false,
	isOrgAdmin: false,
	hasOrgSettingsAccess: false,
	token: null,
	loginLoop: 0,
	isTokenExpired: false,
	redirectLink: null,
	currentUser: null,
	organisationsList: null,
	currentOrganisation: {},
	tiny_mce_api_key: "",
	usersWithVertexRoles: [],
	lastActive: null,
	isRefreshing: false,
});

const getters = {
	canSendProposal: (state) => {
		return state.isSuperAdmin && !state.isAdmin && !state.isConsultant; // Only actual super_admin can send
	},
	canExportLeads: (state) => {
		return !state.isConsultant; // SuperAdmins and Admin Support can export, Consultants cannot
	},
	isGlobalSuperAdmin: (state) => {
		return state.isSuperAdmin && !state.isAdmin && !state.isConsultant;
	},
	canEditContentLibrary: (state) => {
		return state.isSuperAdmin && !state.isConsultant;
	}
}

const actions = {
	resetState({ commit }) {
		commit("resetState")
	},

	logout({ commit, state }) {

		let logoutUrl = `${app.config.globalProperties.$config.getLoginLink()}&logout=true`

		if (!state.isSuperAdmin && !state.isAdmin && !state.isConsultant) {
			logoutUrl = `${logoutUrl}&message=Unauthorized access. Please contact administrator.`;
		}

		if (state.isTokenExpired) {
			logoutUrl = `${logoutUrl}&error=TOKEN_EXPIRE`
		}

		if (!state.isAuthenticated) {
			logoutUrl = `${app.config.globalProperties.$config.getLoginLink()}&logout=true`;
		}

		if (state.redirectLink != null) {
			logoutUrl = `${logoutUrl}&redirectTo=${app.config.globalProperties.$config.getServerHost()}${state.redirectLink}`;
		}

		commit("resetState");

		window.location.href = logoutUrl
	},

	async refreshToken({ commit, state }) {
		if (state.isRefreshing || !state.accessToken) return;
		commit("setRefreshing", true);
		try {
			const response = await app.config.globalProperties.$axios.post("/auth/token/refresh", {
				access_token: state.accessToken
			});
			if (response.data && response.data.status && response.data.user) {
				const newAccessToken = response.data.user.access_token;
				const decodedToken = jwt_decode(newAccessToken);
				commit("setAccessToken", newAccessToken);
				commit("setJwtPayload", decodedToken);
			}
		} catch (error) {
			console.error("Token refresh failed:", error);
		} finally {
			commit("setRefreshing", false);
		}
	},
};

const mutations = {
	resetState(state) {
		Object.assign(state, defaultState());
	},

	setAccessToken(state, accessToken) {
		state.accessToken = accessToken;
		state.isTokenExpired = false;
	},

	setRefreshing(state, isRefreshing) {
		state.isRefreshing = isRefreshing;
	},

	setJwtPayload(state, payload) {
		state.jwtPayload = payload;
		state.tokenExpiry = payload.expires_at;
		state.lastActive = new Date().getTime();

		const activeOrgUuid = payload.organisation_uuid;
		let isOrgAdmin = false;
		let isSparkAdmin = payload.roles ? (payload.roles.includes("admin_support") || payload.roles.includes("spark_admin")) : false;
		let isSparkConsultant = payload.roles ? (payload.roles.includes("consultant") || payload.roles.includes("spark_consultant")) : false;

		// Evaluate organisation-scoped admin and products/roles for the active organisation (XSurfLog Style)
		if (payload && payload.organisation_role) {
			for (const orgRole of payload.organisation_role) {
				const orgUuid = orgRole.organisation_uuid || orgRole.orguuid || orgRole.org_uuid;
				if (orgUuid === activeOrgUuid || orgRole.organisation_id === payload.organisation_id) {
					if (orgRole.is_organisation_admin) {
						isOrgAdmin = true;
					}
					if (orgRole.products) {
						for (const product of orgRole.products) {
							if (product.product === "spark" && product.roles) {
								if (product.roles.includes("admin_support") || product.roles.includes("spark_admin")) {
									isSparkAdmin = true;
								}
								if (product.roles.includes("consultant") || product.roles.includes("spark_consultant")) {
									isSparkConsultant = true;
								}
							}
						}
					}
				}
			}
		}

		state.isOrgAdmin = isOrgAdmin;
		state.isAdmin = isSparkAdmin;
		state.isConsultant = isSparkConsultant;
		state.isSuperAdmin = (payload.roles && payload.roles.includes("super_admin")) || isSparkAdmin || isSparkConsultant;

		let isSup = payload.roles ? payload.roles.includes("super_admin") : false;
		if (isSup) {
			state.hasOrgSettingsAccess = true;
		} else if (state.isAdmin && state.isOrgAdmin) {
			state.hasOrgSettingsAccess = true;
		} else {
			state.hasOrgSettingsAccess = false;
		}

		state.isGeneralUser = !state.isSuperAdmin && !state.isAdmin && !state.isConsultant;

		//console.log(payload)

		state.currentUser = {
			id: payload.user_id,
			uuid: payload.user_uuid,
			first_name: payload.first_name,
			email: payload.email,
			last_name: payload.last_name,
			organisation: {
				id: payload.organisation_role != null ? payload.organisation_role[0].organisation_id : 0,
				orguuid: payload.organisation_uuid,
				name: payload.organisation_role != null ? payload.organisation_role[0].organisation : "",
			}
		}

		if (!state.currentOrganisation || !state.currentOrganisation.id) {
			state.currentOrganisation = {
				id: state.currentUser.organisation.id,
				orguuid: state.currentUser.organisation.orguuid,
				name: state.currentUser.organisation.name,
			}
		}
	},

	authenticated(state, authenticated) {
		state.isAuthenticated = authenticated;
	},

	setTinyMCEAPIKey(state, tiny_mce_api_key) {
		state.tiny_mce_api_key = tiny_mce_api_key;
	},

	setIsTokenExpired(state) {
		state.isTokenExpired = true;
	},

	setRedirectLink(state, link) {
		state.redirectLink = link
	},

	setOrganisationsList(state, orgs) {
		state.organisationsList = orgs
	},

	setCurrentOrganisation(state, org) {
		state.currentOrganisation = org
	},

	setCurrentUser(state, user) {
		state.currentUser = user
	},

	setUsersWithVertexRoles(state, users) {
		state.usersWithVertexRoles = users || [];
	},

	updateLastActive(state) {
		state.lastActive = new Date().getTime();
	},
};

export default {
	name: "auth",
	namespaced: true,
	state,
	getters,
	actions,
	mutations,
};
